import { types } from "mobx-state-tree";
import { flow } from "modules/common/models/flow";
import { Notificator } from "modules/common/models/notificator";
import { Queryable } from "modules/common/models/queryable";
import { SectionCollapser } from "modules/common/models/section-collapser";
import { Transport } from "modules/common/models/transport";
import { buildCacheStorage } from "modules/common/services/cache";
import { apiUrls } from "modules/common/services/communication/urls";
import { nameof } from "modules/common/services/typescript";
import { PlanScale } from "modules/spending/workload-plan/models/plan-scale";
import { ProjectPlanResponse } from "./project-plan-builder";

const NAME = "ProjectPlanStore";
const cache = buildCacheStorage(NAME);

const zoomKey = () => nameof((s: ProjectPlanStoreSnapshotType) => s.zoom) as string;
const scaleKey = () => nameof((s: ProjectPlanStoreSnapshotType) => s.scale) as string;
const dayMonthsKey = () => nameof((s: ProjectPlanStoreSnapshotType) => s.monthsOnPageDay) as string;
const weekMonthsKey = () => nameof((s: ProjectPlanStoreSnapshotType) => s.monthsOnPageWeek) as string;

/**
 * Глубина показа настраивается отдельно для каждого масштаба: в дневной сетке месяц
 * занимает втрое больше места, чем в недельной. Верхняя граница совпадает с
 * GetProjectPlanQuery.MAX_MONTHS на бэке.
 */
export const MONTHS_LIMITS: { [key in PlanScale]: { min: number; max: number; deflt: number } } = {
    day: { min: 2, max: 6, deflt: 3 },
    week: { min: 2, max: 12, deflt: 6 },
};

const clampMonths = (scale: PlanScale, value: number) => {
    const limits = MONTHS_LIMITS[scale];
    return Math.min(limits.max, Math.max(limits.min, value));
};

/**
 * Стор страницы «Плановый график работ» отвечает только за транспорт и настройки вида:
 * страница ничего не редактирует, поэтому ни диффа, ни истории правок здесь нет.
 */
export const ProjectPlanStore = types
    .compose(
        Transport,
        Notificator,
        Queryable,
        types.model({
            month: types.number,
            year: types.number,
            zoom: types.number,
            /** Масштаб сетки: колонка-день или неделя целиком */
            scale: types.enumeration<PlanScale>("PlanScale", ["day", "week"]),
            monthsOnPageDay: types.number,
            monthsOnPageWeek: types.number,
            /** Сколько месяцев реально пришло в последнем ответе сервера */
            loadedMonths: types.number,
            loader: types.number,
            collapser: SectionCollapser,
        })
    )
    .views((self) => ({
        get isLoading() {
            return self.loader > 0;
        },

        get transform() {
            return +(self.zoom / 100).toFixed(2);
        },

        get monthsOnPage() {
            return self.scale === "week" ? self.monthsOnPageWeek : self.monthsOnPageDay;
        },

        /**
         * Грузим по максимуму обоих масштабов — тогда переключение день/неделя
         * и уменьшение глубины показа обходятся без запроса к серверу.
         */
        get monthsRequired() {
            return Math.max(self.monthsOnPageDay, self.monthsOnPageWeek);
        },

        get needsReload() {
            return self.loadedMonths < Math.max(self.monthsOnPageDay, self.monthsOnPageWeek);
        },
    }))
    .actions((self) => ({
        setPeriod(year: number, month: number) {
            self.year = year;
            self.month = month;
        },

        setZoom(value: number) {
            cache.set(zoomKey(), value);
            self.zoom = value;
        },

        setScale(value: PlanScale) {
            cache.set(scaleKey(), value);
            self.scale = value;
        },

        /** Глубина показа запоминается отдельно для дневного и недельного масштаба */
        setMonthsOnPage(value: number) {
            const months = clampMonths(self.scale, value);

            if (self.scale === "week") {
                cache.set(weekMonthsKey(), months);
                self.monthsOnPageWeek = months;
            } else {
                cache.set(dayMonthsKey(), months);
                self.monthsOnPageDay = months;
            }
        },

        fetch: flow(function* (year?: number, month?: number) {
            self.loader++;

            const monthsCount = self.monthsRequired;

            try {
                const data: ProjectPlanResponse = yield self.transport.get<ProjectPlanResponse>(
                    apiUrls.projectPlan.range,
                    {
                        params: {
                            year: year || self.year,
                            month: month || self.month,
                            monthsCount,
                        },
                    }
                );

                self.loadedMonths = monthsCount;

                return data;
            } catch (er) {
                self.notify.error(er);
                return null;
            } finally {
                self.loader--;
            }
        }),
    }))
    .named(NAME);

export type ProjectPlanStoreType = typeof ProjectPlanStore.Type;
export type ProjectPlanStoreSnapshotType = typeof ProjectPlanStore.SnapshotType;

export const initialState = (): ProjectPlanStoreSnapshotType => {
    const now = new Date();

    return {
        month: now.getMonth() + 1,
        year: now.getFullYear(),
        zoom: cache.get(zoomKey(), 100),
        scale: cache.get<PlanScale>(scaleKey(), "week"),
        monthsOnPageDay: clampMonths("day", cache.get(dayMonthsKey(), MONTHS_LIMITS.day.deflt)),
        monthsOnPageWeek: clampMonths("week", cache.get(weekMonthsKey(), MONTHS_LIMITS.week.deflt)),
        loadedMonths: 0,
        loader: 0,
        collapser: { opened: {} },
        pureQuery: "",
        query: "",
    };
};
